import React, {createContext, useContext, useState, useEffect, useRef, useMemo} from 'react';
import {useAuthState} from '../../../shared/services/firebase';
import {subscribeToRecentChangelogs, subscribeToAllChangelogs} from '../services/changelogApi';

const ChangelogContext = createContext();

export const ChangelogProvider = ({children}) => {
  const {isAuthenticated} = useAuthState();
  const [recentEntries, setRecentEntries] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allLoading, setAllLoading] = useState(false);
  const allUnsubRef = useRef(null);

  // Always subscribe to recent entries for sidebar widget
  useEffect(() => {
    if (!isAuthenticated) return;

    const unsubscribe = subscribeToRecentChangelogs(
      4,
      (entries) => {
        setRecentEntries(entries);
        setLoading(false);
      },
      (error) => {
        console.error('Changelog subscription error:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [isAuthenticated]);

  // Subscribe to all entries when page mounts
  useEffect(() => {
    if (!isAuthenticated) return;

    setAllLoading(true);
    const unsubscribe = subscribeToAllChangelogs(
      (entries) => {
        setAllEntries(entries);
        setAllLoading(false);
      },
      (error) => {
        console.error('Changelog full subscription error:', error);
        setAllLoading(false);
      }
    );
    allUnsubRef.current = unsubscribe;

    return () => {
      unsubscribe();
      allUnsubRef.current = null;
    };
  }, [isAuthenticated]);

  const value = useMemo(() => ({
    recentEntries, allEntries, loading, allLoading
  }), [recentEntries, allEntries, loading, allLoading]);

  return (
    <ChangelogContext.Provider value={value}>
      {children}
    </ChangelogContext.Provider>
  );
};

export const useChangelog = () => {
  const context = useContext(ChangelogContext);
  if (!context) {
    throw new Error('useChangelog must be used within a ChangelogProvider');
  }
  return context;
};
